import type { Session } from '../../api/types.ts';

/**
 * The realtime side of session-derived Stage/cockpit content — the join QR+URL, the marquee's
 * default text, the lower-third's venue name (SPEC AC-OPS-6, AC-OPS-7). The session content-type
 * lifecycle (`strapi-template/.../session/lifecycles.js`) already publishes a `session.updated`
 * event on the `settings` channel with a deliberately partial payload (only the fields the Stage
 * and guest phones care about — never `songsPerRotation`/`turnaroundSec`, which queue-rotation
 * owns); this is the pure merge rule for applying one of those frames to what `useSessionInfo.ts`
 * already has, kept out of the hook so the "only merge into a session we recognise" rule is
 * testable without a real SSE connection.
 */
export type SessionUpdatePayload = Pick<Session, 'documentId' | 'isActive' | 'requestMode'> &
  Partial<Pick<Session, 'marqueeText' | 'stageMessage' | 'joinCode' | 'venue' | 'name'>>;

/**
 * Never merges into `null` — a partial payload cannot safely stand in for the required fields
 * (`songsPerRotation`, `turnaroundSec`, ...) a fresh `Session` needs, so an update that arrives
 * before the initial `GET /api/sessions` completes is dropped rather than fabricating a
 * half-populated record. Also ignores an update for a session other than the one currently
 * loaded (a stale event racing a session change) rather than silently overwriting it.
 */
export function applySessionUpdate(current: Session | null, payload: SessionUpdatePayload): Session | null {
  if (!current) return null;
  if (current.documentId !== payload.documentId) return current;
  return { ...current, ...payload };
}
